import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Building2, TrendingUp, DollarSign, Target } from 'lucide-react';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody } from '../ui/Card';
import { IndustryCreate, IndustryBenchmarks } from '../../types/threePhase';

// Validation schema
const industrySchema = z.object({
  name: z.string().min(3, 'Industry name must be at least 3 characters'),
  classification: z.string().min(3, 'Classification is required'),
  marketSizeUSD: z.number().positive('Market size must be positive'),
  marketGrowthRate: z.number().min(-100).max(1000, 'Growth rate must be between -100% and 1000%'),
  valueChain: z.array(z.string()).optional(),
  technologyTrends: z.array(z.string()).optional(),
  regulatoryEnvironment: z.string().optional(),
  commonPainPoints: z.array(z.string()).optional(),
  benchmarks: z.object({
    gross_margin: z.number().min(0).max(100),
    operating_margin: z.number().min(-100).max(100),
    roe: z.number().min(-100).max(500),
    revenue_growth: z.number().min(-100).max(1000),
  }),
});

interface IndustryResearchFormProps {
  caseId: string;
  initialData?: IndustryCreate;
  onSubmit: (data: IndustryCreate) => Promise<void>;
  onCancel?: () => void;
}

const IndustryResearchForm: React.FC<IndustryResearchFormProps> = ({
  caseId,
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [valueChainInput, setValueChainInput] = useState('');
  const [trendsInput, setTrendsInput] = useState('');
  const [painPointsInput, setPainPointsInput] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IndustryCreate>({
    resolver: zodResolver(industrySchema),
    defaultValues: initialData || {
      name: '',
      classification: '',
      marketSizeUSD: 0,
      marketGrowthRate: 0,
      valueChain: [],
      technologyTrends: [],
      regulatoryEnvironment: '',
      commonPainPoints: [],
      benchmarks: {
        gross_margin: 0,
        operating_margin: 0,
        roe: 0,
        revenue_growth: 0,
      },
    },
  });

  const valueChain = watch('valueChain') || [];
  const technologyTrends = watch('technologyTrends') || [];
  const commonPainPoints = watch('commonPainPoints') || [];

  const addValueChainItem = () => {
    if (valueChainInput.trim()) {
      setValue('valueChain', [...valueChain, valueChainInput.trim()]);
      setValueChainInput('');
    }
  };

  const removeValueChainItem = (index: number) => {
    setValue('valueChain', valueChain.filter((_, i) => i !== index));
  };

  const addTrendItem = () => {
    if (trendsInput.trim()) {
      setValue('technologyTrends', [...technologyTrends, trendsInput.trim()]);
      setTrendsInput('');
    }
  };

  const removeTrendItem = (index: number) => {
    setValue('technologyTrends', technologyTrends.filter((_, i) => i !== index));
  };

  const addPainPointItem = () => {
    if (painPointsInput.trim()) {
      setValue('commonPainPoints', [...commonPainPoints, painPointsInput.trim()]);
      setPainPointsInput('');
    }
  };

  const removePainPointItem = (index: number) => {
    setValue('commonPainPoints', commonPainPoints.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (data: IndustryCreate) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      toast.success('Industry research saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save industry research');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-semibold">Industry Overview</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <Input
            label="Industry Name"
            placeholder="e.g., Technology - Cloud Computing"
            {...register('name')}
            error={errors.name?.message}
            required
          />

          <Input
            label="Classification"
            placeholder="e.g., Technology, Healthcare, Manufacturing"
            {...register('classification')}
            error={errors.classification?.message}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Market Size (USD)"
              type="number"
              placeholder="500000000000"
              {...register('marketSizeUSD', { valueAsNumber: true })}
              error={errors.marketSizeUSD?.message}
              required
            />

            <Input
              label="Market Growth Rate (%)"
              type="number"
              step="0.1"
              placeholder="15.5"
              {...register('marketGrowthRate', { valueAsNumber: true })}
              error={errors.marketGrowthRate?.message}
              required
            />
          </div>

          <Textarea
            label="Regulatory Environment"
            placeholder="Describe regulatory landscape..."
            rows={3}
            {...register('regulatoryEnvironment')}
            error={errors.regulatoryEnvironment?.message}
          />
        </CardBody>
      </Card>

      {/* Value Chain */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Value Chain Components</h3>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Add value chain component..."
              value={valueChainInput}
              onChange={(e) => setValueChainInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addValueChainItem())}
            />
            <Button type="button" onClick={addValueChainItem} variant="secondary">
              Add
            </Button>
          </div>
          {valueChain.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {valueChain.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeValueChainItem(index)}
                    className="text-primary-500 hover:text-primary-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Technology Trends */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-secondary-500" />
            <h3 className="text-lg font-semibold">Technology Trends</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Add technology trend..."
              value={trendsInput}
              onChange={(e) => setTrendsInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTrendItem())}
            />
            <Button type="button" onClick={addTrendItem} variant="secondary">
              Add
            </Button>
          </div>
          {technologyTrends.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {technologyTrends.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-50 text-secondary-700 rounded-full text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeTrendItem(index)}
                    className="text-secondary-500 hover:text-secondary-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Common Pain Points */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-semibold">Common Pain Points</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Add pain point..."
              value={painPointsInput}
              onChange={(e) => setPainPointsInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPainPointItem())}
            />
            <Button type="button" onClick={addPainPointItem} variant="secondary">
              Add
            </Button>
          </div>
          {commonPainPoints.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {commonPainPoints.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removePainPointItem(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Industry Benchmarks */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            <h3 className="text-lg font-semibold">Industry Benchmarks (%)</h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Gross Margin"
              type="number"
              step="0.1"
              placeholder="70.0"
              {...register('benchmarks.gross_margin', { valueAsNumber: true })}
              error={errors.benchmarks?.gross_margin?.message}
              required
            />

            <Input
              label="Operating Margin"
              type="number"
              step="0.1"
              placeholder="25.0"
              {...register('benchmarks.operating_margin', { valueAsNumber: true })}
              error={errors.benchmarks?.operating_margin?.message}
              required
            />

            <Input
              label="Return on Equity (ROE)"
              type="number"
              step="0.1"
              placeholder="20.0"
              {...register('benchmarks.roe', { valueAsNumber: true })}
              error={errors.benchmarks?.roe?.message}
              required
            />

            <Input
              label="Revenue Growth"
              type="number"
              step="0.1"
              placeholder="15.0"
              {...register('benchmarks.revenue_growth', { valueAsNumber: true })}
              error={errors.benchmarks?.revenue_growth?.message}
              required
            />
          </div>
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={isSubmitting}>
          Save Industry Research
        </Button>
      </div>
    </form>
  );
};

export default IndustryResearchForm;
